<div class="ed-container">

  <!-- ── HEADER ─────────────────────────────────────────────────────────── -->
  <header class="ed-header">
    <div class="ed-header-left">
      <button (click)="goBack()" class="ed-back-btn">
        <i class="fa-solid fa-arrow-left"></i>
        <span>Back</span>
      </button>
      <div class="ed-header-branding">
        <div class="ed-header-icon-wrap">
          <i class="fa-solid fa-user-tie"></i>
        </div>
        <div class="ed-header-meta">
          <h1 class="ed-title">Executant Details</h1>
          <p class="ed-subtitle fe-desktop-only">Overview of all projects and performance of this executant</p>
          @if (!isLoading() && allProjects().length > 0) {
            <p class="ed-m-header-sub fe-mobile-only">{{ executantName() }}</p>
            <div class="ed-breadcrumb fe-desktop-only">
              <i class="fa-regular fa-map"></i>
              <span>{{primaryBlock()}}</span>
              <i class="fa-solid fa-chevron-right"></i>
              <span>{{primaryGP()}} GP</span>
              <i class="fa-solid fa-chevron-right"></i>
              <span class="bc-active">{{executantName()}}</span>
            </div>
          }
        </div>
      </div>
    </div>
    <div class="ed-header-actions">
      <button (click)="goHome()" class="ed-home-btn" title="Back to Home">
        <i class="fa-solid fa-house"></i>
        <span>Home</span>
      </button>
      <button (click)="exportPdf()" class="ed-export-btn" title="Export PDF" [disabled]="filteredProjects().length === 0">
        <i class="fa-solid fa-file-pdf"></i>
        <span>Export PDF</span>
      </button>
    </div>
  </header>

  @if (isLoading()) {
    <div class="ed-loading">
      <i class="fa-solid fa-spinner fa-spin"></i>
      <p>Loading...</p>
    </div>
  }

  @if (!isLoading()) {
    <div class="ed-page-body">

      <div class="fe-desktop-only ed-last-updated-row">
        <div></div>
        <div class="ed-last-updated-text">
          Last Updated: Today, {{ currentTime() | date:'shortTime' }}
          <button class="ed-refresh-btn"><i class="fa-solid fa-rotate"></i></button>
        </div>
      </div>

      @if (allProjects().length === 0) {
        <div class="ed-empty">
          <i class="fa-solid fa-folder-open"></i>
          <p>No projects found for this executant.</p>
        </div>
      }

      @if (allProjects().length > 0) {

        <!-- ── MOBILE ──────────────────────────────────────────────────── -->
        <div class="fe-mobile-only ed-mobile-stack">

          <div class="ed-m-profile">
            <div class="ed-m-avatar">{{ executantInitials() }}</div>
            <h2 class="ed-m-name">{{ executantName() || '—' }}</h2>
            <p class="ed-m-role">Executant</p>
            @if (executantNumber()) {
              <p class="ed-m-phone"><i class="fa-solid fa-phone"></i> {{ executantNumber() }}</p>
            }
            @if (executantMembers().length) {
              <div class="ed-members ed-m-members">
                @for (m of executantMembers(); track m) {
                  <button type="button" class="ed-member-chip" (click)="viewPerson(m)">
                    <i class="fa-solid fa-user"></i> {{ m }}
                  </button>
                }
              </div>
            }
          </div>

          <div class="ed-m-kpi">
            <div class="ed-m-kpi-row">
              <div class="ed-m-kpi-tile blue">
                <i class="fa-solid fa-folder-open"></i>
                <span class="ed-m-kpi-val">{{ totalProjects() }}</span>
              </div>
              <div class="ed-m-kpi-tile amber">
                <i class="fa-solid fa-indian-rupee-sign"></i>
                <span class="ed-m-kpi-val">{{ costCompact(totalCost()) }}</span>
              </div>
              <div class="ed-m-kpi-tile green">
                <i class="fa-solid fa-circle-check"></i>
                <span class="ed-m-kpi-val">{{ completedCount() }}</span>
              </div>
              <div class="ed-m-kpi-tile slate">
                <i class="fa-solid fa-circle"></i>
                <span class="ed-m-kpi-val">{{ plannedCount() }}</span>
              </div>
            </div>
            <div class="ed-m-kpi-row">
              <div class="ed-m-kpi-tile orange">
                <i class="fa-regular fa-clock"></i>
                <span class="ed-m-kpi-val">{{ inProgressCount() }}</span>
              </div>
              <div class="ed-m-kpi-tile red">
                <i class="fa-solid fa-circle-xmark"></i>
                <span class="ed-m-kpi-val">{{ delayedCount() }}</span>
              </div>
              <div class="ed-m-kpi-tile purple">
                <i class="fa-solid fa-flag"></i>
                <span class="ed-m-kpi-val">{{ rivalryCount() }}</span>
              </div>
              <div class="ed-m-kpi-tile" [ngClass]="'risk-' + riskLevel().toLowerCase()">
                <i class="fa-solid fa-shield-halved"></i>
                <span class="ed-m-kpi-val">{{ riskShort() }}</span>
              </div>
            </div>
          </div>

          <button type="button" class="ed-m-summary-btn" (click)="openHmDrawer()">
            <span class="ed-m-summary-title"><i class="fa-solid fa-chart-pie"></i> Performance Summary</span>
            <span class="ed-m-summary-tap"><i class="fa-solid fa-chevron-up"></i> Details</span>
          </button>

          <h3 class="ed-m-section-title">Assigned Projects ({{ totalProjects() }})</h3>

          <div class="ed-m-project-list">
            @for (p of allProjects(); track p.id) {
              <article class="ed-m-proj-card">
                <h4 class="ed-m-proj-name">{{ p.name }}</h4>
                <p class="ed-m-proj-meta">
                  <i class="fa-solid fa-location-dot"></i>
                  {{ p.gp || p.village || p.loc || '—' }} GP
                </p>
                @if (p.scheme) {
                  <p class="ed-m-proj-meta scheme">
                    <i class="fa-solid fa-tag"></i>
                    {{ p.scheme }}
                  </p>
                }
                <p class="ed-m-proj-cost">₹{{ p.cost | number:'1.2-2' }}L</p>
                <span class="ed-m-proj-status" [ngClass]="statusChipClass(p.status)">
                  {{ statusLabel(p.status) }}
                </span>
                <button type="button" class="ed-m-proj-view" (click)="openProjectDetail(p)">
                  View Details <i class="fa-solid fa-arrow-right"></i>
                </button>
              </article>
            }
          </div>

        </div>

        <!-- ── DESKTOP ───────────────────────────────────────────────────── -->
        <div class="fe-desktop-only ed-desktop-stack">

        <!-- ── PROFILE CARD ──────────────────────────────────────────────── -->
        <div class="ed-profile-card">
          <!-- Avatar + Identity -->
          <div class="ed-identity">
            <div class="ed-avatar">{{executantInitials()}}</div>
            <div>
              <h2 class="ed-name">{{executantName() || '—'}}</h2>
              <div class="ed-type">Executant ({{executantType()}})</div>
              @if (executantNumber()) {
                <div class="ed-phone"><i class="fa-solid fa-phone"></i> {{executantNumber()}}</div>
              }
              @if (executantMembers().length) {
                <div class="ed-members">
                  <span class="ed-members-lbl">View individual:</span>
                  @for (m of executantMembers(); track m) {
                    <button type="button" class="ed-member-chip" (click)="viewPerson(m)">
                      <i class="fa-solid fa-user"></i> {{ m }}
                    </button>
                  }
                </div>
              }
            </div>
          </div>

          <div class="ed-divider-v"></div>

          <!-- Stats -->
          <div class="ed-stats-row">
            <div class="ed-stat-block">
              <div class="ed-stat-lbl">Total Projects</div>
              <div class="ed-stat-val">{{totalProjects()}}</div>
            </div>
            <div class="ed-divider-v-sm"></div>
            <div class="ed-stat-block">
              <div class="ed-stat-lbl">Total Est. Cost</div>
              <div class="ed-stat-val cost-val">₹{{totalCost() | number:'1.2-2'}} L</div>
            </div>
            <div class="ed-divider-v-sm"></div>
            <div class="ed-stat-block">
              <div class="ed-stat-lbl">Completed</div>
              <div class="ed-stat-val green-val">{{completedCount()}}</div>
            </div>
            <div class="ed-divider-v-sm"></div>
            <div class="ed-stat-block">
              <div class="ed-stat-lbl">Not Started</div>
              <div class="ed-stat-val" style="color:#64748b">{{plannedCount()}}</div>
            </div>
            <div class="ed-divider-v-sm"></div>
            <div class="ed-stat-block">
              <div class="ed-stat-lbl">In Progress</div>
              <div class="ed-stat-val amber-val">{{inProgressCount()}}</div>
            </div>
            <div class="ed-divider-v-sm"></div>
            <div class="ed-stat-block">
              <div class="ed-stat-lbl">Delayed</div>
              <div class="ed-stat-val red-val">{{delayedCount()}}</div>
            </div>
            <div class="ed-divider-v-sm"></div>
            <div class="ed-stat-block">
              <div class="ed-stat-lbl">Rivalry Issues</div>
              <div class="ed-stat-val rivalry-val">{{rivalryCount()}}</div>
            </div>
          </div>

          <div class="ed-divider-v"></div>

          <!-- Risk Level -->
          <div class="ed-risk-block">
            <div class="ed-risk-label">Risk Level <i class="fa-solid fa-circle-info" style="font-size:10px;color:#94a3b8"></i></div>
            <div class="ed-risk-value" [class]="'risk-' + riskLevel().toLowerCase()">
              <i class="fa-solid fa-circle" style="font-size:9px"></i>
              {{riskLevel()}} ({{riskScore()}})
            </div>
            <div class="ed-risk-score">Score: {{riskScore()}} / 100</div>
          </div>
        </div>

        <!-- ── ASSIGNED PROJECTS SUMMARY ───────────────────────────────────── -->
        <div class="ed-assigned-panel">
          <div class="ed-assigned-header">
            <div class="ed-assigned-title-row">
              <i class="fa-solid fa-list-check"></i>
              <span class="ed-assigned-title">Assigned Projects</span>
              <span class="ed-assigned-db-badge">
                <i class="fa-solid fa-database"></i>
                {{ totalProjects() }} from database
              </span>
            </div>
            <div class="ed-assigned-sub">
              All {{ totalProjects() }} project{{ totalProjects() !== 1 ? 's' : '' }} assigned to
              <strong>{{ executantName() }}</strong>
            </div>
          </div>

          <div class="ed-assigned-list">
            @for (p of allProjects(); track p.id; let i = $index) {
              <div class="ed-assigned-row">
                <span class="ed-asgn-sl">{{ i + 1 }}</span>
                <span class="ed-asgn-name">{{ p.name }}</span>
                <span class="ed-asgn-gp">{{ p.gp || p.village || p.loc || '–' }} GP</span>
                <span class="ed-asgn-scheme">{{ p.scheme || '–' }}</span>
                <span class="ed-asgn-cost">₹{{ p.cost | number:'1.0-0' }} L</span>
                <span class="ed-asgn-status" [ngClass]="{
                  'asgn-completed': p.status === 'Completed',
                  'asgn-inprogress': p.status === 'In Progress',
                  'asgn-stuck': p.status === 'Stuck',
                  'asgn-planned': p.status === 'Planned'
                }">
                  @if (p.status === 'Completed') { <i class="fa-solid fa-circle-check"></i> }
                  @else if (p.status === 'In Progress') { <i class="fa-regular fa-clock"></i> }
                  @else if (p.status === 'Stuck') { <i class="fa-solid fa-circle-xmark"></i> }
                  @else { <i class="fa-regular fa-circle"></i> }
                  {{ statusLabel(p.status) }}
                </span>
              </div>
            }
          </div>

          <div class="ed-assigned-footer">
            <span class="ed-asgn-foot-item">
              <i class="fa-solid fa-circle-check" style="color:#16a34a"></i>
              {{ completedCount() }} Completed
            </span>
            <span class="ed-asgn-foot-sep"></span>
            <span class="ed-asgn-foot-item">
              <i class="fa-regular fa-clock" style="color:#d97706"></i>
              {{ inProgressCount() }} In Progress
            </span>
            <span class="ed-asgn-foot-sep"></span>
            <span class="ed-asgn-foot-item">
              <i class="fa-solid fa-circle-xmark" style="color:#dc2626"></i>
              {{ delayedCount() }} Delayed
            </span>
            <span class="ed-asgn-foot-sep"></span>
            <span class="ed-asgn-foot-item">
              <i class="fa-regular fa-circle" style="color:#94a3b8"></i>
              {{ plannedCount() }} Not Started
            </span>
          </div>
        </div>

        <!-- ── 4 ANALYSIS CARDS ──────────────────────────────────────────── -->
        <div class="ed-analysis-grid">

          <!-- 1. Performance Summary (Donut) -->
          <div class="ed-analysis-card">
            <h4 class="ed-card-title">Performance Summary</h4>
            <div class="ed-donut-wrap">
              <svg class="ed-donut-svg" viewBox="0 0 120 120">
                <circle cx="60" cy="60" r="40" fill="transparent" stroke="#f1f5f9" stroke-width="20"/>
                @for (seg of donutSegments(); track seg.label) {
                  <circle cx="60" cy="60" r="40" fill="transparent"
                    [attr.stroke]="seg.color" stroke-width="20"
                    [attr.stroke-dasharray]="seg.dasharray"
                    [attr.stroke-dashoffset]="seg.dashoffset"
                    transform="rotate(-90 60 60)"/>
                }
              </svg>
              <div class="ed-donut-legend">
                @for (seg of donutSegments(); track seg.label) {
                  <div class="ed-legend-item">
                    <i class="fa-solid fa-circle" [style.color]="seg.color"></i>
                    <span class="ed-legend-label">{{seg.label}}</span>
                    <span class="ed-legend-count">{{seg.count}} ({{seg.pct}}%)</span>
                  </div>
                }
              </div>
            </div>
            @if (delayPercent() >= 40) {
              <div class="ed-alert-bar ed-alert-red">
                <i class="fa-solid fa-triangle-exclamation"></i>
                {{delayPercent()}}% projects are delayed
              </div>
            } @else if (delayPercent() > 0) {
              <div class="ed-alert-bar ed-alert-amber">
                <i class="fa-solid fa-triangle-exclamation"></i>
                {{delayPercent()}}% projects are delayed
              </div>
            }
          </div>

          <!-- 2. Issue Breakdown -->
          <div class="ed-analysis-card">
            <h4 class="ed-card-title">Issue Breakdown</h4>
            <div class="ed-issue-list">
              @for (item of issueBreakdown(); track item.label) {
                <div class="ed-issue-row">
                  <div class="ed-issue-left">
                    <i [class]="'fa-solid ' + item.icon" [style.color]="item.color"></i>
                    <span>{{item.label}}</span>
                  </div>
                  <div class="ed-issue-right">
                    <span [class.issue-nonzero]="item.count > 0">{{item.count}}</span>
                    <span class="ed-issue-pct">({{item.pct}}%)</span>
                  </div>
                </div>
              }
            </div>
          </div>

          <!-- 3. Update Discipline -->
          <div class="ed-analysis-card">
            <h4 class="ed-card-title">Update Discipline</h4>
            <div class="ed-update-list">
              <div class="ed-update-row">
                <div class="ed-update-left">
                  <i class="fa-regular fa-calendar"></i>
                  <span>Last Updated</span>
                </div>
                <div class="ed-update-val" [class.val-red]="(lastUpdatedDays() ?? 0) > 5" [class.val-amber]="(lastUpdatedDays() ?? 0) > 2 && (lastUpdatedDays() ?? 0) <= 5">
                  {{getLastUpdatedLabel(lastUpdatedDays())}}
                </div>
              </div>
              <div class="ed-update-row">
                <div class="ed-update-left">
                  <i class="fa-regular fa-clock"></i>
                  <span>Average Update Gap</span>
                </div>
                <div class="ed-update-val">
                  {{lastUpdatedDays() !== null ? lastUpdatedDays() + ' days' : 'N/A'}}
                </div>
              </div>
              <div class="ed-update-row">
                <div class="ed-update-left">
                  <i class="fa-solid fa-chart-line"></i>
                  <span>Update Consistency</span>
                </div>
                <div class="ed-update-val">
                  <span class="ed-consistency" [class]="'uc-' + updateConsistency().toLowerCase()">{{updateConsistency()}}</span>
                </div>
              </div>
            </div>
            @if (updateConsistency() === 'Poor') {
              <div class="ed-alert-bar ed-alert-amber">
                <i class="fa-solid fa-triangle-exclamation"></i>
                Irregular updates. Needs monitoring.
              </div>
            }
          </div>

          <!-- 4. Projects by Location -->
          <div class="ed-analysis-card">
            <h4 class="ed-card-title">Projects by Location</h4>
            <div class="ed-location-list">
              @for (item of gpBreakdown(); track item.gp; let i = $index) {
                <div class="ed-location-row">
                  <div class="ed-loc-dot" [class]="'loc-' + (i % 4)"></div>
                  <div class="ed-loc-info">
                    <div class="ed-loc-gp">{{item.gp}} GP</div>
                    <div class="ed-loc-count">{{item.count}} Project{{item.count !== 1 ? 's' : ''}}</div>
                  </div>
                </div>
              }
            </div>
          </div>

        </div>

        <!-- ── PROJECTS TABLE ─────────────────────────────────────────────── -->
        <div class="ed-table-section">
          <div class="ed-table-header-row">
            <h3 class="ed-table-section-title">Projects Under This Executant ({{filteredProjects().length}})</h3>
            <div class="ed-table-controls">
              <div class="ed-search-box">
                <i class="fa-solid fa-magnifying-glass"></i>
                <input type="search" placeholder="Search Project"
                       [ngModel]="searchTerm()" (ngModelChange)="searchTerm.set($event)" />
              </div>
              <select class="ed-status-select" [ngModel]="statusFilter()" (ngModelChange)="statusFilter.set($event)">
                @for (s of statusOptions; track s) {
                  <option [value]="s">{{s || 'All Status'}}</option>
                }
              </select>
            </div>
          </div>

          <div class="ed-table-scroll">
            <table class="ed-table">
              <thead>
                <tr>
                  <th>SL</th>
                  <th>PROJECT NAME</th>
                  <th>GP / VILLAGE</th>
                  <th>SCHEME</th>
                  <th>EST. COST (₹)</th>
                  <th>STATUS</th>
                  <th>ISSUE TYPE</th>
                  <th>START DATE</th>
                  <th>TARGET DATE</th>
                  <th>LAST UPDATED</th>
                  <th>ACTION</th>
                </tr>
              </thead>
              <tbody>
                @if (filteredProjects().length === 0) {
                  <tr><td colspan="11" class="ed-td-empty">No projects match your filter.</td></tr>
                }
                @for (p of filteredProjects(); track p.id; let i = $index) {
                  <tr class="ed-tr">
                    <td class="td-sl">{{i + 1}}</td>
                    <td class="td-project">{{p.name}}</td>
                    <td class="td-gp">{{p.gp || p.village || p.loc || '—'}}</td>
                    <td class="td-scheme">{{p.scheme || '—'}}</td>
                    <td class="td-cost">₹{{p.cost | number:'1.2-2'}} L</td>
                    <td class="td-status">
                      <span class="ed-status-chip" [ngClass]="statusChipClass(p.status)">
                        @if (p.status === 'Completed') { <i class="fa-solid fa-circle-check"></i> }
                        @else if (p.status === 'In Progress') { <i class="fa-regular fa-clock"></i> }
                        @else if (p.status === 'Stuck') { <i class="fa-solid fa-circle-xmark"></i> }
                        @else { <i class="fa-regular fa-circle"></i> }
                        {{statusLabel(p.status)}}
                      </span>
                    </td>
                    <td class="td-issue">
                      @if (getIssueType(p) !== '–') {
                        <span class="ed-issue-tag">
                          <i [class]="'fa-solid ' + issueIcon(getIssueType(p))" [style.color]="issueColor(getIssueType(p))"></i>
                          {{getIssueType(p)}}
                        </span>
                      } @else {
                        <span class="td-no-issue">
                          <i class="fa-solid fa-circle" style="color:#e2e8f0;font-size:8px"></i> No Issue
                        </span>
                      }
                    </td>
                    <td class="td-date">{{p.start || '—'}}</td>
                    <td class="td-date" [class.date-overdue]="p.targetDate && p.targetDate < today">
                      {{p.targetDate || p.end || '—'}}
                    </td>
                    <td class="td-date">
                      {{getDaysLabel(p.updatedAt || p.photoUploadedAt)}}
                    </td>
                    <td class="td-action">
                      <button class="ed-action-chevron"><i class="fa-solid fa-chevron-right"></i></button>
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        </div>

        <!-- ── BOTTOM 3 COLUMNS ───────────────────────────────────────────── -->
        <div class="ed-bottom-grid">

          <!-- Trend chart (SVG) -->
          <div class="ed-bottom-card">
            <h4 class="ed-card-title">Trend (Last 6 Months)</h4>
            <svg class="ed-trend-svg" viewBox="0 0 400 140" preserveAspectRatio="none">
              <!-- Grid lines -->
              @for (y of [10, 45, 80]; track y) {
                <line x1="40" [attr.y1]="y" x2="390" [attr.y2]="y" stroke="#f1f5f9" stroke-width="1"/>
              }
              <line x1="40" y1="110" x2="390" y2="110" stroke="#e2e8f0" stroke-width="1"/>
              <!-- Y-axis -->
              <line x1="40" y1="10" x2="40" y2="110" stroke="#e2e8f0" stroke-width="1"/>
              <!-- Month labels -->
              @for (month of trendData().months; track month; let i = $index) {
                <text [attr.x]="40 + i * 70" y="128" text-anchor="middle" font-size="9" fill="#94a3b8">{{month}}</text>
              }
              <!-- Y labels -->
              @for (label of [trendMaxVal(), 0]; track label; let i = $index) {
                <text x="35" [attr.y]="i === 0 ? 14 : 114" text-anchor="end" font-size="8" fill="#94a3b8">{{label}}</text>
              }
              <!-- Series lines -->
              @for (s of trendData().series; track s.label) {
                <path [attr.d]="trendPath(s.values, trendMaxVal())"
                      [attr.stroke]="s.color" fill="none" stroke-width="2"
                      stroke-linejoin="round" stroke-linecap="round"/>
                @for (v of s.values; track $index; let i = $index) {
                  <circle [attr.cx]="40 + i * 70"
                          [attr.cy]="110 - (v / trendMaxVal()) * 100"
                          r="3" [attr.fill]="s.color"/>
                }
              }
            </svg>
            <div class="ed-trend-legend">
              @for (s of trendData().series; track s.label) {
                <span class="ed-trend-leg-item">
                  <i class="fa-solid fa-circle" [style.color]="s.color" style="font-size:7px"></i>
                  {{s.label}}
                </span>
              }
            </div>
          </div>

          <!-- Insights -->
          <div class="ed-bottom-card">
            <h4 class="ed-card-title">Insights</h4>
            <div class="ed-insights-list">
              @if (insights().length === 0) {
                <div class="ed-no-data">All projects are progressing well.</div>
              }
              @for (item of insights(); track item.text) {
                <div class="ed-insight-row">
                  <i [class]="'fa-solid ' + item.icon" [style.color]="item.color"></i>
                  <span>{{item.text}}</span>
                </div>
              }
            </div>
          </div>

          <!-- Recommended Actions -->
          <div class="ed-bottom-card">
            <h4 class="ed-card-title">Recommended Action</h4>
            <div class="ed-actions-list">
              @for (action of recommendedActions(); track action.text) {
                <div class="ed-action-row">
                  <i [class]="'fa-solid ' + action.icon" style="color:#64748b;width:14px;flex-shrink:0"></i>
                  <span>{{action.text}}</span>
                </div>
              }
            </div>
          </div>

        </div>

        </div><!-- /fe-desktop-only -->

      }
    </div>
  }

  @if (hmDrawerOpen()) {
    <div class="ed-sheet-overlay fe-mobile-only" (click)="closeHmDrawer()">
      <div class="ed-sheet" (click)="$event.stopPropagation()">
        <div class="ed-sheet-handle"></div>
        <div class="ed-sheet-head">
          <span class="ed-sheet-title"><i class="fa-solid fa-chart-pie"></i> Performance Summary</span>
          <button type="button" class="ed-sheet-close" (click)="closeHmDrawer()" aria-label="Close">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>
        <div class="ed-sheet-body">
          <div class="ed-sheet-section">
            <h4 class="ed-sheet-section-title">Performance Summary</h4>
            <div class="ed-donut-wrap">
              <svg class="ed-donut-svg" viewBox="0 0 120 120">
                <circle cx="60" cy="60" r="40" fill="transparent" stroke="#f1f5f9" stroke-width="20"/>
                @for (seg of donutSegments(); track seg.label) {
                  <circle cx="60" cy="60" r="40" fill="transparent"
                    [attr.stroke]="seg.color" stroke-width="20"
                    [attr.stroke-dasharray]="seg.dasharray"
                    [attr.stroke-dashoffset]="seg.dashoffset"
                    transform="rotate(-90 60 60)"/>
                }
              </svg>
              <div class="ed-donut-legend">
                @for (seg of donutSegments(); track seg.label) {
                  <div class="ed-legend-item">
                    <i class="fa-solid fa-circle" [style.color]="seg.color"></i>
                    <span class="ed-legend-label">{{ seg.label }}</span>
                    <span class="ed-legend-count">{{ seg.count }} ({{ seg.pct }}%)</span>
                  </div>
                }
              </div>
            </div>
            @if (delayPercent() >= 40) {
              <div class="ed-alert-bar ed-alert-red">
                <i class="fa-solid fa-triangle-exclamation"></i>
                {{ delayPercent() }}% projects are delayed
              </div>
            } @else if (delayPercent() > 0) {
              <div class="ed-alert-bar ed-alert-amber">
                <i class="fa-solid fa-triangle-exclamation"></i>
                {{ delayPercent() }}% projects are delayed
              </div>
            }
          </div>
          <div class="ed-sheet-section">
            <h4 class="ed-sheet-section-title">Issue Breakdown</h4>
            <div class="ed-issue-list">
              @for (item of issueBreakdown(); track item.label) {
                <div class="ed-issue-row">
                  <div class="ed-issue-left">
                    <i [class]="'fa-solid ' + item.icon" [style.color]="item.color"></i>
                    <span>{{ item.label }}</span>
                  </div>
                  <div class="ed-issue-right">
                    <span [class.issue-nonzero]="item.count > 0">{{ item.count }}</span>
                    <span class="ed-issue-pct">({{ item.pct }}%)</span>
                  </div>
                </div>
              }
            </div>
          </div>
          <div class="ed-sheet-section">
            <h4 class="ed-sheet-section-title">Update Discipline</h4>
            <div class="ed-update-list">
              <div class="ed-update-row">
                <div class="ed-update-left"><i class="fa-regular fa-calendar"></i><span>Last Updated</span></div>
                <div class="ed-update-val" [class.val-red]="(lastUpdatedDays() ?? 0) > 5" [class.val-amber]="(lastUpdatedDays() ?? 0) > 2 && (lastUpdatedDays() ?? 0) <= 5">
                  {{ getLastUpdatedLabel(lastUpdatedDays()) }}
                </div>
              </div>
              <div class="ed-update-row">
                <div class="ed-update-left"><i class="fa-regular fa-clock"></i><span>Average Update Gap</span></div>
                <div class="ed-update-val">{{ lastUpdatedDays() !== null ? lastUpdatedDays() + ' days' : 'N/A' }}</div>
              </div>
              <div class="ed-update-row">
                <div class="ed-update-left"><i class="fa-solid fa-chart-line"></i><span>Update Consistency</span></div>
                <div class="ed-update-val">
                  <span class="ed-consistency" [class]="'uc-' + updateConsistency().toLowerCase()">{{ updateConsistency() }}</span>
                </div>
              </div>
            </div>
          </div>
          <div class="ed-sheet-section">
            <h4 class="ed-sheet-section-title">Projects by Location</h4>
            <div class="ed-location-list">
              @for (item of gpBreakdown(); track item.gp; let i = $index) {
                <div class="ed-location-row">
                  <div class="ed-loc-dot" [class]="'loc-' + (i % 4)"></div>
                  <div class="ed-loc-info">
                    <div class="ed-loc-gp">{{ item.gp }} GP</div>
                    <div class="ed-loc-count">{{ item.count }} Project{{ item.count !== 1 ? 's' : '' }}</div>
                  </div>
                </div>
              }
            </div>
          </div>
        </div>
      </div>
    </div>
  }

  @if (selectedProject(); as p) {
    <div class="ed-sheet-overlay fe-mobile-only" (click)="closeProjectDetail()">
      <div class="ed-sheet" (click)="$event.stopPropagation()">
        <div class="ed-sheet-handle"></div>
        <div class="ed-sheet-head">
          <span class="ed-sheet-title">Project Details</span>
          <button type="button" class="ed-sheet-close" (click)="closeProjectDetail()" aria-label="Close">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>
        <div class="ed-sheet-body">
          <h4 class="ed-proj-sheet-name">{{ p.name }}</h4>
          <div class="ed-proj-detail-grid">
            <div class="ed-proj-detail-row"><span class="lbl">GP / Village</span><span class="val">{{ p.gp || p.village || p.loc || '—' }}</span></div>
            <div class="ed-proj-detail-row"><span class="lbl">Scheme</span><span class="val">{{ p.scheme || '—' }}</span></div>
            <div class="ed-proj-detail-row"><span class="lbl">Est. Cost</span><span class="val">₹{{ p.cost | number:'1.2-2' }} L</span></div>
            <div class="ed-proj-detail-row"><span class="lbl">Status</span><span class="val"><span class="ed-status-chip" [ngClass]="statusChipClass(p.status)">{{ statusLabel(p.status) }}</span></span></div>
            <div class="ed-proj-detail-row"><span class="lbl">Issue Type</span><span class="val">{{ getIssueType(p) }}</span></div>
            <div class="ed-proj-detail-row"><span class="lbl">Start Date</span><span class="val">{{ p.start || '—' }}</span></div>
            <div class="ed-proj-detail-row"><span class="lbl">Target Date</span><span class="val">{{ p.targetDate || p.end || '—' }}</span></div>
            <div class="ed-proj-detail-row"><span class="lbl">Last Updated</span><span class="val">{{ getDaysLabel(p.updatedAt || p.photoUploadedAt) }}</span></div>
            @if (p.remarks) {
              <div class="ed-proj-detail-row full"><span class="lbl">Remarks</span><span class="val">{{ p.remarks }}</span></div>
            }
          </div>
        </div>
      </div>
    </div>
  }

</div>
